import { api, readTotalCount } from '../api/client';
import {
	API_ADMIN_HISTORY_URL,
	API_CARD_HISTORY_URL,
	API_CHANGE_REVERT_URL,
	API_CHANGE_URL,
	API_COLLECTION_HISTORY_URL,
	API_ITEM_HISTORY_URL,
	API_USER_HISTORY_URL,
	API_MY_RECENTLY_DELETED_URL,
	API_COLLECTION_RECENTLY_DELETED_URL,
	API_ADMIN_RECENTLY_DELETED_URL,
} from '../utils';

/**
 * The change log — every mutation to any element or config, who did it, when,
 * from what to what (keepr-api docs/SCHEMA.md § History). Entries arrive
 * DESCRIBED: element labels, choice labels, lookup titles and a one-sentence
 * `summary` are rendered server-side by one describer, so this layer never
 * re-derives a label. An entry:
 *
 *   { _id, at, requestId, action, op, target: { type, id, label, card? },
 *     scope, actor: { type, userId, user, keyId, keyName, automationId,
 *     automationName, runId, task }, causedBy, derived,
 *     changes: [ { path, label, from?, to?, fromDisplay?, toDisplay?, at?,
 *                  reorder?, field?, redacted?, labelMissing? } ],
 *     refs, revert, revertedBy, revertable, summary,
 *     before?, after? (single-entry endpoint only),
 *     ip?, forwardedFor?, userAgent? (admin callers only) }
 *
 * Gates are the API's: an item's history needs WRITE on the item (a reader
 * gets 403), a collection's feed manage, a card's the card's edit gate, a
 * user's self-or-admin, the admin feed admin. Reverts are session-only.
 */

// Axios lower-cases response header names, but a mocked client (or a proxy)
// may not — the header is the pagination contract (the adminAutomations
// service's convention).
/**
 * The listing filters every history endpoint accepts. Empty values are
 * dropped rather than sent (an unknown `type` is a 400 on the API).
 *   type      one target type or a comma list (item, card, grant, …)
 *   action    one action or a comma list (item.update, …)
 *   actor     a user id
 *   since / until   ISO-8601
 *   requestId
 *   derived   true | false — include system recomputes (default per endpoint)
 *   limit / skip
 */
function listParams({ type, action, actor, since, until, requestId, derived, limit, skip } = {}) {
	const params = {};
	if (type) params.type = Array.isArray(type) ? type.join(',') : type;
	if (action) params.action = Array.isArray(action) ? action.join(',') : action;
	if (actor) params.actor = actor;
	if (since) params.since = since;
	if (until) params.until = until;
	if (requestId) params.requestId = requestId;
	if (derived === true || derived === false) params.derived = derived ? 'true' : 'false';
	if (limit !== undefined) params.limit = limit;
	if (skip !== undefined) params.skip = skip;
	return params;
}

async function list(url, filters) {
	const { data, headers } = await api.get(url, { params: listParams(filters) });
	const rows = Array.isArray(data) ? data : [];
	return { rows, total: readTotalCount(headers, rows.length) };
}

/**
 * One item's history: its own entries plus the relationship/tag/attachment
 * entries that reference it. Derived included by default. Also resolves
 * `deleted` — whether the item is deleted NOW (keepr-api `X-Item-Deleted`):
 * one opened from its deletion may have been restored since. null from an API
 * that does not say.
 */
export async function fetchItemHistory(itemId, filters) {
	const { data, headers } = await api.get(API_ITEM_HISTORY_URL(itemId), { params: listParams(filters) });
	const rows = Array.isArray(data) ? data : [];
	return { rows, total: readTotalCount(headers, rows.length), deleted: readItemDeleted(headers) };
}

/** `X-Item-Deleted` as a boolean, whatever case the header arrives in; null when absent. */
function readItemDeleted(headers) {
	if (!headers) return null;
	const key = Object.keys(headers).find(name => name.toLowerCase() === 'x-item-deleted');
	const raw = key === undefined ? undefined : String(headers[key]).toLowerCase();
	if (raw === 'true') return true;
	if (raw === 'false') return false;
	return null;
}

/** A collection's feed (manage): items, cards, settings, sharing, filters, automations, share links. Derived excluded by default. */
export const fetchCollectionHistory = (collectionId, filters) => list(API_COLLECTION_HISTORY_URL(collectionId), filters);

/** A card definition's history (same gate as editing it). */
export const fetchCardHistory = (cardId, filters) => list(API_CARD_HISTORY_URL(cardId), filters);

/** A user's own history: profile, preferences, favorites, API keys, tags, avatar (self or admin). */
export const fetchUserHistory = (userId, filters) => list(API_USER_HISTORY_URL(userId), filters);

/** Everything, newest first, with caller metadata (admin only). */
export const fetchAdminHistory = filters => list(API_ADMIN_HISTORY_URL, filters);

/** One entry in full — before/after snapshots included. */
export async function fetchChange(changeId) {
	const { data } = await api.get(API_CHANGE_URL(changeId));
	return data;
}

/**
 * Revert an entry: a NEW write through the normal path that undoes it.
 *   { force }  overwrite paths that changed since (409 `conflicts` otherwise),
 *              or delete a created record that has later changes (409 `modified_since`)
 *   { paths }  update entries only — revert a subset of the changed paths
 * Resolves { change, conflicts, restored? }; rejects with the axios error,
 * whose response.data carries `code` and, for 409s, `conflicts` / `later`.
 */
export async function revertChange(changeId, { force = false, paths } = {}) {
	const body = {};
	if (force) body.force = true;
	if (Array.isArray(paths) && paths.length) body.paths = paths;
	const { data } = await api.post(API_CHANGE_REVERT_URL(changeId), body);
	return data;
}

/**
 * "Recently deleted" (keepr-api docs/SCHEMA.md § History › Recently deleted,
 * delete audit F5). Each row is a history entry — the NEWEST delete of
 * something still deleted — plus:
 *   restorableUntil  ISO date (an account delete's collection: its 30-day
 *                    purge) or null (restorable while its history is kept)
 *   blocked          { code, message } | null — a refusal the list can see
 *                    already (an item whose card is deleted)
 *   collection       { _id, name } | null
 *   deletedWith?     an account delete's counts (collections only)
 * Restoring is revertChange(row._id): no restore endpoint of its own.
 */
async function listDeleted(url, { kind, collectionId, limit, skip } = {}) {
	const params = {};
	if (kind) params.kind = Array.isArray(kind) ? kind.join(',') : kind;
	if (collectionId) params.collection_id = collectionId;
	if (limit !== undefined) params.limit = limit;
	if (skip !== undefined) params.skip = skip;
	const { data, headers } = await api.get(url, { params });
	const rows = Array.isArray(data) ? data : [];
	return { rows, total: readTotalCount(headers, rows.length) };
}

/** The caller's own deleted collections (owner, session-only). */
export const fetchMyRecentlyDeleted = filters => listDeleted(API_MY_RECENTLY_DELETED_URL, filters);

/** A collection's deleted cards, element sets and items (manage). */
export const fetchCollectionRecentlyDeleted = (collectionId, filters) => listDeleted(API_COLLECTION_RECENTLY_DELETED_URL(collectionId), filters);

/** Everything restorable, account-delete purge windows included (admin). */
export const fetchAdminRecentlyDeleted = filters => listDeleted(API_ADMIN_RECENTLY_DELETED_URL, filters);
